import React, { useMemo, useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { LineChart } from '../../components/charts';
import { RefreshCw, Package, BarChart2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { currencySymbol } from '../../utils/currencySymbol';
import ShopifySyncStatus from '../../components/ShopifySyncStatus';

const formatNumber = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString();
};

const formatShortNumber = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const v = n / 1_000_000;
    const s = abs < 10_000_000 ? v.toFixed(1) : Math.round(v).toString();
    return `${s}M`;
  }
  if (abs >= 1_000) {
    const v = n / 1_000;
    const s = abs < 10_000 ? v.toFixed(1) : Math.round(v).toString();
    return `${s}K`;
  }
  return n.toString();
};

const formatMonthLabel = (ymd) => {
  try {
    const d = new Date(ymd);
    return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  } catch {
    return ymd;
  }
};

const formatDayShort = (ymd) => {
  try {
    const d = new Date(ymd);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}-${mm}`;
  } catch {
    return ymd;
  }
};

export default function ShopifyProductPerformance() {
  const { selectedBrand } = useAuth();
  const CUR = currencySymbol(selectedBrand?.currency);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 29); // default 30 days
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [metric, setMetric] = useState('revenue'); // revenue | orders | items
  const [selectedRange, setSelectedRange] = useState('30d'); // default selected quick range

  const [dailyRows, setDailyRows] = useState([]);
  const [topRows, setTopRows] = useState([]);
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [categoryMonthlyRows, setCategoryMonthlyRows] = useState([]);
  const [categoryDailyRows, setCategoryDailyRows] = useState([]);
  const [selectedSectionTab, setSelectedSectionTab] = useState('brand');

  const lineData = useMemo(() => {
    const byDate = new Map();
    for (const r of dailyRows) {
      const d = String(r.day).slice(0, 10);
      if (!byDate.has(d)) byDate.set(d, { revenue: 0, orders: 0, items: 0 });
      const acc = byDate.get(d);
      acc.revenue += Number(r.revenue || 0);
      acc.orders += Number(r.orders_count || 0);
      acc.items += Number(r.items_count || 0);
    }
    return Array.from(byDate.entries())
      .sort((a,b) => a[0].localeCompare(b[0]))
      .map(([label, v]) => ({ label, value: metric === 'revenue' ? v.revenue : metric === 'orders' ? v.orders : v.items }));
  }, [dailyRows, metric]);

  const totals = useMemo(() => {
    let revenue = 0, orders = 0, items = 0;
    for (const r of dailyRows) {
      revenue += Number(r.revenue || 0);
      orders  += Number(r.orders_count || 0);
      items   += Number(r.items_count || 0);
    }
    return { revenue, orders, items };
  }, [dailyRows]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const headers = { 'X-Brand-ID': brandId };
      const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
      const res = await fetch(`${base}/api/shopify/products/daily?${params.toString()}`, { credentials: 'include', headers });
      const js = await res.json().catch(() => ({}));
      const rows = res.ok && js?.rows ? js.rows : [];
      setDailyRows(rows);
      const byProduct = new Map();
      for (const r of rows) {
        const key = `${r.product_id || ''}__${r.sku || ''}`;
        if (!byProduct.has(key)) {
          byProduct.set(key, {
            product_id: r.product_id,
            sku: r.sku,
            brand_name: r.brand_name,
            product_name: r.product_name,
            revenue: 0, orders_count: 0, items_count: 0
          });
        }
        const acc = byProduct.get(key);
        acc.revenue += Number(r.revenue || 0);
        acc.orders_count += Number(r.orders_count || 0);
        acc.items_count += Number(r.items_count || 0);
      }
      setTopRows(Array.from(byProduct.values()).sort((a,b) => b.revenue - a.revenue).slice(0, 25));
      const resM = await fetch(`${base}/api/shopify/products/monthly`, { credentials: 'include', headers });
      const jsM = await resM.json().catch(() => ({}));
      const rowsM = resM.ok && jsM?.rows ? jsM.rows : [];
      setMonthlyRows(rowsM);
      // Category monthly performance
      const resCatM = await fetch(`${base}/api/shopify/product-category/monthly`, { credentials: 'include', headers });
      const jsCatM = await resCatM.json().catch(() => ({}));
      const rowsCatM = resCatM.ok && jsCatM?.rows ? jsCatM.rows : [];
      setCategoryMonthlyRows(rowsCatM);
      // Category daily (selected range)
      try {
        const resCatD = await fetch(`${base}/api/shopify/product-category/daily?${params.toString()}`, { credentials: 'include', headers });
        const jsCatD = await resCatD.json().catch(() => ({}));
        const rowsCatD = resCatD.ok && jsCatD?.rows ? jsCatD.rows : [];
        setCategoryDailyRows(rowsCatD);
      } catch {}
    } catch (e) {
      console.error('ShopifyProductPerformance fetch error', e);
      setDailyRows([]); setTopRows([]); setMonthlyRows([]); setCategoryMonthlyRows([]); setCategoryDailyRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <ShopifySyncStatus />
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-3">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-3">
            <img
              src="/shopify_glyph.svg"
              alt="Shopify"
              className="w-10 h-10 object-contain rounded-lg"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Product Performance</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg">
              {[
                { id: '7d', label: '7 Days' },
                { id: '30d', label: '30 Days' },
                { id: 'thisMonth', label: 'This Month' },
                { id: 'lastMonth', label: 'Last Month' },
                { id: 'all', label: 'All Time' },
              ].map(opt => (
                <button
                  key={opt.id}
                  className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                    selectedRange === opt.id ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:bg-white hover:text-gray-900'
                  }`}
                  onClick={() => {
                    const today = new Date();
                    let s = new Date(today);
                    let e = new Date(today);
                    switch (opt.id) {
                      case '7d': s.setDate(today.getDate() - 6); break;
                      case '30d': s.setDate(today.getDate() - 29); break;
                      case 'thisMonth': s = new Date(today.getFullYear(), today.getMonth(), 1); break;
                      case 'lastMonth': {
                        const firstOfThis = new Date(today.getFullYear(), today.getMonth(), 1);
                        e = new Date(firstOfThis.getTime() - 86400000);
                        s = new Date(e.getFullYear(), e.getMonth(), 1);
                        break;
                      }
                      case 'all': s = new Date(today.getFullYear() - 3, 0, 1); break;
                      default: break;
                    }
                    const toYmd = (d) => d.toISOString().slice(0, 10);
                    setStartDate(toYmd(s));
                    setEndDate(toYmd(e));
                    setSelectedRange(opt.id);
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              className="p-2 text-gray-600 hover:text-gray-900 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-60"
              onClick={fetchData}
              disabled={loading}
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-4">
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Revenue</p>
                <p className="text-lg font-semibold mt-1">{CUR}{formatShortNumber(totals.revenue)}</p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-50">
                <BarChart2 className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
          </Card>
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Orders</p>
                <p className="text-lg font-semibold mt-1">{formatNumber(totals.orders)}</p>
              </div>
              <div className="p-2 rounded-lg bg-indigo-50">
                <BarChart2 className="w-4 h-4 text-indigo-600" />
              </div>
            </div>
          </Card>
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Items</p>
                <p className="text-lg font-semibold mt-1">{formatNumber(totals.items)}</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-50">
                <Package className="w-4 h-4 text-amber-600" />
              </div>
            </div>
          </Card>
        </div>

        <div className="flex items-center justify-between mt-5 mb-3">
          <div className="text-sm font-medium text-gray-800">Daily Trend</div>
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            <button className={`px-2.5 py-1.5 rounded-lg text-xs ${metric === 'revenue' ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-700 hover:text-gray-900'}`} onClick={() => setMetric('revenue')}>Revenue</button>
            <button className={`px-2.5 py-1.5 rounded-lg text-xs ${metric === 'orders' ? 'bg-white shadow-sm text-blue-700' : 'text-gray-700 hover:text-gray-900'}`} onClick={() => setMetric('orders')}>Orders</button>
            <button className={`px-2.5 py-1.5 rounded-lg text-xs ${metric === 'items' ? 'bg-white shadow-sm text-amber-700' : 'text-gray-700 hover:text-gray-900'}`} onClick={() => setMetric('items')}>Items</button>
          </div>
        </div>
        <div className="h-72">
          <LineChart 
            data={{
              labels: lineData.map(d => d.label),
              datasets: [{
                label: metric === 'revenue' ? 'Revenue' : metric === 'orders' ? 'Orders' : 'Items',
                data: lineData.map(d => d.value),
                borderColor: metric === 'revenue' ? 'rgb(16, 185, 129)' : metric === 'orders' ? 'rgb(37, 99, 235)' : 'rgb(217, 119, 6)',
                backgroundColor: metric === 'revenue' ? 'rgba(16, 185, 129, 0.1)' : (metric === 'orders' ? 'rgba(37, 99, 235, 0.1)' : 'rgba(217, 119, 6, 0.1)'),
                fill: true,
                tension: 0.4
              }]
            }}
          />
        </div>
      </div>

      {/* Section Selector (Brand / Category / Product) */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-3">
        <div className="flex items-center gap-2">
          {[
            { id: 'brand', label: 'Brand' },
            { id: 'category', label: 'Category' },
            { id: 'product', label: 'Product' },
          ].map(opt => (
            <button
              key={opt.id}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                selectedSectionTab === opt.id
                  ? 'bg-gray-900 text-white shadow-sm'
                  : 'text-gray-700 bg-gray-50 hover:bg-white hover:text-gray-900 border border-gray-200'
              }`}
              onClick={() => setSelectedSectionTab(opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {selectedSectionTab === 'category' && (
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="text-lg font-semibold">Categories — Daily (Selected Range)</div>
        </div>
        {(() => {
          const datesSet = new Set((categoryDailyRows || []).map(r => String(r.day).slice(0, 10)));
          const dates = Array.from(datesSet).sort();
          const byCat = new Map();
          const totals = new Map();
          for (const r of (categoryDailyRows || [])) {
            const d = String(r.day).slice(0, 10);
            const cat = r.product_category || 'Unknown';
            const rev = Number(r.revenue || 0);
            if (!byCat.has(cat)) byCat.set(cat, {});
            byCat.get(cat)[d] = (byCat.get(cat)[d] || 0) + rev;
            totals.set(cat, (totals.get(cat) || 0) + rev);
          }
          const cats = Array.from(byCat.keys()).sort((a,b) => (totals.get(b)||0)-(totals.get(a)||0)).slice(0, 20);
          if (cats.length === 0 || dates.length === 0) return <div className="text-sm text-gray-500">No data.</div>;
          return (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b align-bottom">
                    <th className="py-2 pr-4">Category</th>
                    {dates.map(d => (<th key={d} className="py-2 pr-4 text-xs text-gray-500 whitespace-nowrap">{formatDayShort(d)}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {cats.map(cat => (
                    <tr key={cat} className="border-b hover:bg-gray-50">
                      <td className="py-2 pr-4 font-medium">{cat}</td>
                      {dates.map(d => (
                        <td key={`${cat}-${d}`} className="py-2 pr-4">{CUR}{formatShortNumber((byCat.get(cat)?.[d]) || 0)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>
      )}

      {selectedSectionTab === 'category' && (
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="text-lg font-semibold">Category Monthly Performance</div>
        </div>
        {(() => {
          const monthsSet = new Set((categoryMonthlyRows || []).map(r => String(r.month).slice(0, 10)));
          const months = Array.from(monthsSet).sort();
          const valuesByCat = new Map();
          const totals = new Map();
          for (const r of (categoryMonthlyRows || [])) {
            const m = String(r.month).slice(0, 10);
            const cat = r.product_category || 'Unknown';
            const val = metric === 'revenue'
              ? Number(r.revenue || 0)
              : metric === 'orders'
              ? Number(r.orders_count || 0)
              : Number(r.items_count || 0);
            if (!valuesByCat.has(cat)) valuesByCat.set(cat, {});
            valuesByCat.get(cat)[m] = (valuesByCat.get(cat)[m] || 0) + val;
            totals.set(cat, (totals.get(cat) || 0) + val);
          }
          const cats = Array.from(valuesByCat.keys()).sort((a, b) => (totals.get(b) || 0) - (totals.get(a) || 0)).slice(0, 20);
          if (cats.length === 0) {
            return <div className="text-sm text-gray-500">No data.</div>;
          }
          const fmt = (x) => metric === 'revenue' ? `${CUR}${formatShortNumber(x)}` : formatNumber(x);
          return (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b align-bottom">
                    <th className="py-2 pr-4">Category</th>
                    {months.map(m => (
                      <th key={m} className="py-2 pr-4">{formatMonthLabel(m)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cats.map(cat => (
                    <tr key={cat} className="border-b hover:bg-gray-50">
                      <td className="py-2 pr-4 font-medium">{cat}</td>
                      {months.map(m => (
                        <td key={`${cat}-${m}`} className="py-2 pr-4">{fmt((valuesByCat.get(cat)?.[m]) || 0)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>
      )}

      {selectedSectionTab === 'brand' && (
      <>
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="text-lg font-semibold">Brands — Daily (Selected Range)</div>
        </div>
        {(() => {
          const datesSet = new Set((dailyRows || []).map(r => String(r.day).slice(0, 10)));
          const dates = Array.from(datesSet).sort();
          const byBrand = new Map();
          const totals = new Map();
          for (const r of (dailyRows || [])) {
            const d = String(r.day).slice(0, 10);
            const brand = r.brand_name || 'Unknown';
            const rev = Number(r.revenue || 0);
            if (!byBrand.has(brand)) byBrand.set(brand, {});
            byBrand.get(brand)[d] = (byBrand.get(brand)[d] || 0) + rev;
            totals.set(brand, (totals.get(brand) || 0) + rev);
          }
          const brands = Array.from(byBrand.keys()).sort((a,b) => (totals.get(b)||0)-(totals.get(a)||0)).slice(0,15);
          if (brands.length === 0 || dates.length === 0) return <div className="text-sm text-gray-500">No data.</div>;
          return (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b align-bottom">
                    <th className="py-2 pr-4">Brand</th>
                    {dates.map(d => (<th key={d} className="py-2 pr-4 text-xs text-gray-500 whitespace-nowrap">{formatDayShort(d)}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {brands.map(brand => (
                    <tr key={brand} className="border-b hover:bg-gray-50">
                      <td className="py-2 pr-4 font-medium">{brand}</td>
                      {dates.map(d => (
                        <td key={`${brand}-${d}`} className="py-2 pr-4">{CUR}{formatShortNumber((byBrand.get(brand)?.[d]) || 0)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="text-lg font-semibold">Top Brands (Monthly Revenue)</div>
        </div>
        {(() => {
          const monthsSet = new Set((monthlyRows || []).map(r => String(r.month).slice(0, 10)));
          const months = Array.from(monthsSet).sort();
          const valuesByBrand = new Map();
          const totals = new Map();
          for (const r of (monthlyRows || [])) {
            const m = String(r.month).slice(0, 10);
            const brand = r.brand_name || 'Unknown';
            const rev = Number(r.revenue || 0);
            if (!valuesByBrand.has(brand)) valuesByBrand.set(brand, {});
            valuesByBrand.get(brand)[m] = (valuesByBrand.get(brand)[m] || 0) + rev;
            totals.set(brand, (totals.get(brand) || 0) + rev);
          }
          const brands = Array.from(valuesByBrand.keys()).sort((a, b) => (totals.get(b) || 0) - (totals.get(a) || 0)).slice(0, 15);
          if (brands.length === 0) {
            return <div className="text-sm text-gray-500">No data.</div>;
          }
          return (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b align-bottom">
                    <th className="py-2 pr-4">Brand</th>
                    {months.map(m => (
                      <th key={m} className="py-2 pr-4">{formatMonthLabel(m)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {brands.map(brand => (
                    <tr key={brand} className="border-b hover:bg-gray-50">
                      <td className="py-2 pr-4 font-medium">{brand}</td>
                      {months.map(m => (
                        <td key={`${brand}-${m}`} className="py-2 pr-4">{CUR}{formatShortNumber((valuesByBrand.get(brand)?.[m]) || 0)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>
      </>
      )}

      {selectedSectionTab === 'product' && (
      <>
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="text-lg font-semibold">Products — Daily (Selected Range)</div>
        </div>
        {(() => {
          const datesSet = new Set((dailyRows || []).map(r => String(r.day).slice(0, 10)));
          const dates = Array.from(datesSet).sort();
          const byProduct = new Map();
          const totals = new Map();
          for (const r of (dailyRows || [])) {
            const d = String(r.day).slice(0, 10);
            const key = `${r.product_name || r.product_id || '-'}#${r.sku || ''}`;
            const rev = Number(r.revenue || 0);
            if (!byProduct.has(key)) byProduct.set(key, {});
            byProduct.get(key)[d] = (byProduct.get(key)[d] || 0) + rev;
            totals.set(key, (totals.get(key) || 0) + rev);
          }
          const products = Array.from(byProduct.keys()).sort((a,b) => (totals.get(b)||0)-(totals.get(a)||0)).slice(0, 25);
          if (products.length === 0 || dates.length === 0) return <div className="text-sm text-gray-500">No data.</div>;
          return (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b align-bottom">
                    <th className="py-2 pr-4">Product</th>
                    {dates.map(d => (<th key={d} className="py-2 pr-4 text-xs text-gray-500 whitespace-nowrap">{formatDayShort(d)}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {products.map(key => {
                    const name = key.split('#')[0];
                    return (
                      <tr key={key} className="border-b hover:bg-gray-50">
                        <td className="py-2 pr-4 font-medium">{name}</td>
                        {dates.map(d => (
                          <td key={`${key}-${d}`} className="py-2 pr-4">{CUR}{formatShortNumber((byProduct.get(key)?.[d]) || 0)}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="text-lg font-semibold">Top Products (Monthly Revenue)</div>
        </div>
        {(() => {
          const rows = monthlyRows || [];
          const monthsSet = new Set(rows.map(r => String(r.month).slice(0, 10)));
          const months = Array.from(monthsSet).sort();
          const byProd = new Map();
          const totals = new Map();
          for (const r of rows) {
            const key = `${r.product_name || r.product_id || '-'}#${r.sku || ''}`;
            const m = String(r.month).slice(0,10);
            const rev = Number(r.revenue || 0);
            if (!byProd.has(key)) byProd.set(key, {});
            byProd.get(key)[m] = (byProd.get(key)[m] || 0) + rev;
            totals.set(key, (totals.get(key) || 0) + rev);
          }
          const products = Array.from(byProd.keys()).sort((a,b) => (totals.get(b)||0)-(totals.get(a)||0)).slice(0,25);
          if (products.length === 0 || months.length === 0) return <div className="text-sm text-gray-500">No data.</div>;
          return (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b align-bottom">
                    <th className="py-2 pr-4">Product</th>
                    {months.map(m => (<th key={m} className="py-2 pr-4">{formatMonthLabel(m)}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {products.map(key => {
                    const name = key.split('#')[0];
                    return (
                      <tr key={key} className="border-b hover:bg-gray-50">
                        <td className="py-2 pr-4 font-medium">{name}</td>
                        {months.map(m => (
                          <td key={`${key}-${m}`} className="py-2 pr-4">{CUR}{formatShortNumber((byProd.get(key)?.[m]) || 0)}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>
      </>
      )}
    </div>
  );
}


